import PublicHeader from '@/components/ui/public-header';
import Footer from '@/components/ui/footer';
import { getCompanyFullAddress, getCompanyEmails, getProductName } from '@/lib/config';

export default function TermsPage() {
  const productName = getProductName();
  return (
    <div className="flex flex-col min-h-screen">
      <PublicHeader />
      
      <main className="flex-1 bg-surface-marketing">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-content-heading mb-8">Terms of Service</h1>
          <p className="text-content-secondary mb-8">Last updated: January 2025</p>
          
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Agreement to Terms</h2>
              <p className="text-content-body mb-4">
                These Terms of Service ("Terms") govern your use of {productName}'s email campaign platform and related services (the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-content-body">
                If you do not agree to these Terms, please do not use our Service. We reserve the right to modify these Terms at any time with 30 days' notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Description of Service</h2>
              <p className="text-content-body mb-4">
                {productName} provides an email campaign management platform that allows you to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Create and send email campaigns</li>
                <li>Manage contact lists and recipients</li>
                <li>Track email performance and analytics</li>
                <li>Automate email sequences</li>
                <li>Integrate with third-party services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Account Registration</h2>
              <p className="text-content-body mb-4">
                To use our Service, you must:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Be at least 18 years old</li>
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use the Service for business purposes only</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
              <p className="text-content-body mt-4">
                You are responsible for all activities that occur under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Acceptable Use Policy</h2>
              <p className="text-content-body mb-4">
                You agree to use our Service responsibly and in compliance with all applicable laws. You must not:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Send spam, unsolicited emails, or bulk messages</li>
                <li>Use purchased, rented, or scraped email lists</li>
                <li>Send emails without proper consent from recipients</li>
                <li>Include malicious content, viruses, or harmful code</li>
                <li>Impersonate others or misrepresent your identity</li>
                <li>Violate any laws, including anti-spam regulations</li>
                <li>Interfere with or disrupt our Service</li>
                <li>Attempt to gain unauthorized access to our systems</li>
              </ul>
              <p className="text-content-body mt-4">
                All emails must include a clear unsubscribe link and your business contact information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Email Deliverability</h2>
              <p className="text-content-body mb-4">
                We strive to ensure high deliverability rates, but we cannot guarantee that all emails will reach their intended recipients. Email delivery depends on various factors including:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Recipient email provider policies</li>
                <li>Email content and subject lines</li>
                <li>Sender reputation</li>
                <li>Technical configurations</li>
              </ul>
              <p className="text-content-body mt-4">
                We monitor sending metrics and may temporarily limit sending if we detect unusual activity or poor engagement rates.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Pricing and Payment</h2>
              <p className="text-content-body mb-4">
                Our pricing plans are available on our website. By subscribing to a paid plan, you agree to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Pay all fees when due</li>
                <li>Provide accurate billing information</li>
                <li>Authorize automatic renewal unless cancelled</li>
                <li>Pay any applicable taxes</li>
              </ul>
              <p className="text-content-body mt-4">
                Fees are non-refundable except as required by law. We may suspend or terminate your account for non-payment.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Data and Privacy</h2>
              <p className="text-content-body mb-4">
                You retain ownership of your data and content. By using our Service, you grant us the right to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Process and store your data to provide the Service</li>
                <li>Use aggregated, anonymized data to improve our platform</li>
                <li>Access your data as necessary for support and maintenance</li>
              </ul>
              <p className="text-content-body mt-4">
                You are responsible for ensuring you have the right to use and send all data and content through our Service. See our Privacy Policy for details on how we handle your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Intellectual Property</h2>
              <p className="text-content-body mb-4">
                {productName} retains all rights to our platform, software, and related intellectual property. We grant you a limited, non-exclusive license to use our Service as described in these Terms.
              </p>
              <p className="text-content-body">
                You may not reverse engineer, modify, or create derivative works of our Service without our written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Service Availability</h2>
              <p className="text-content-body mb-4">
                We strive to maintain high availability of our Service but cannot guarantee 100% uptime. We may temporarily suspend the Service for:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Scheduled maintenance</li>
                <li>Emergency repairs</li>
                <li>System upgrades</li>
                <li>Security measures</li>
              </ul>
              <p className="text-content-body mt-4">
                We will provide reasonable notice of planned maintenance when possible.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Termination</h2>
              <p className="text-content-body mb-4">
                Either party may terminate this agreement at any time. We may immediately suspend or terminate your account if you:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Violate these Terms or our policies</li>
                <li>Fail to pay fees when due</li>
                <li>Engage in illegal or harmful activities</li>
                <li>Pose a security risk to our Service</li>
              </ul>
              <p className="text-content-body mt-4">
                Upon termination, you will lose access to your account and data. We may retain your data for a reasonable period to comply with legal obligations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Limitation of Liability</h2>
              <p className="text-content-body mb-4">
                To the maximum extent permitted by law, {productName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.
              </p>
              <p className="text-content-body">
                Our total liability to you shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Disclaimers</h2>
              <p className="text-content-body mb-4">
                Our Service is provided "as is" without warranties of any kind. We do not guarantee that the Service will be:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Uninterrupted or error-free</li>
                <li>Free from viruses or harmful components</li>
                <li>Suitable for your specific needs</li>
                <li>Compliant with all applicable laws in your jurisdiction</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Governing Law</h2>
              <p className="text-content-body">
                These Terms are governed by the laws of the State of California, United States. Any disputes will be resolved in the courts of San Francisco County, California.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Contact Information</h2>
              <p className="text-content-body">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 text-content-body">
                <p>Email: <a href={`mailto:${getCompanyEmails().legal}`} className="text-link hover:text-link-hover">{getCompanyEmails().legal}</a></p>
                <p>Address: {getCompanyFullAddress()}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

